import React, { useMemo } from 'react';
// Import styles for this component
import styles from './styles.module.scss';
// Typography components for title and text
import Title from 'components/typografy/title';
import Text from 'components/typografy/text';
// Type definitions
import { IVendorNames, IVendor } from 'types/payment';
// Custom subcomponents
import Item from './item';
import NoData from 'components/no-data';
// React Hook Form for managing form state
import { Controller, useForm } from 'react-hook-form';
// Enum for payment types
import { PaymentType } from 'enums/payment';
// Form for entering PayPal account name
import AccountNameForm from './account-name-form';
// Utility for conditionally combining class names
import classNames from 'classnames';
// Custom Checkbox and Chakra UI Button
import Checkbox from 'components/checkbox';
import { Button } from '@chakra-ui/react';
// Mock data for available amounts
import { vendorData } from './data';
import { toaster } from 'components/chakra-ui/toaster';
// Import API endpoint to create a withdrawal
import { createWithdrawal } from 'api/payment';

// Define the props for this component
const Withdrawal: React.FC<{
    image: string;
    type: IVendorNames;
    title: string;
    onClose: () => void; // accept close function
  }> = ({
    image,
    type,
    title,
    onClose
}) => {
  
  // Loading state of form submission
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Get selected vendor object by its type (vendor name)
  const activeVendor: IVendor = vendorData[type];

  // Setup form with React Hook Form
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<any>({
    reValidateMode: 'onChange' // Revalidate fields when changed
  });

  // Watch values from the form
  const amount = watch(type); // Selected amount
  const confirmPayment = watch('confirmPayPal'); // Confirmation checkbox for PayPal
  const confirmNonRefundable = watch('confirmNonRefundable'); // Confirmation checkbox for non-refundable agreement

  // Calculate how much user will actually receive after fees
  const amountFeesSubtracted = useMemo(() => {
    const hasFees = activeVendor?.withdrawalFee > 0; // Check if withdrawal fee is valid

    if (hasFees) {
      const minFees = activeVendor?.withdrawalMinimumFee; // Get minimum fee
      const feesAmount = activeVendor?.withdrawalFee * amount; // Get fees amount
      const finalFees = feesAmount < minFees ? minFees : feesAmount; // Check if fees amount is less than minimum fee
      const amountFeesSubtracted = Number(amount) - Number(finalFees);

      return isNaN(amountFeesSubtracted) ? 0 : amountFeesSubtracted;
    }

    return amount; // If no fees, return the selected amount

  }, [activeVendor, amount]);

  // Check if there are any validation errors
  const hasError = Object.keys(errors).length > 0;

  // What happens when form is submitted
  const onSubmit = async (data: any) => {
    if (isSubmitting) return; // Prevent multiple submissions
    setIsSubmitting(true); // Set submitting state to true
    try {
      // If PayPal, check if email is valid
      const emailInput = (document.querySelector('input[name="paypalAccount"]') as HTMLInputElement)?.value || '';
      if (type === PaymentType.PAYPAL) {
          // Watch PayPal email value
          const isValidEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailInput);
          if (!isValidEmail) {
              // Show toast modal with warning message 
              toaster.create({
                description: 'Please enter a valid PayPal account (email address)',
                duration: 5000,
                type: 'warning',
                action: {
                  label: 'Close',
                  onClick: () => {}
                }
              });
              // Exit & prevent form submission
              return;
          }
      }
      // Show toast modal "You are being redirected to finalize your withdrawal"
      toaster.create({
        description: 'Your request is being processed... New tab will open right away to finalize your withdrawal.',
        duration: 5000,
        type: 'info',
        action: {
          label: 'Close',
          onClick: () => {}
        }
      });
      // Procced with the form submission - Wait for the API call to create the withdrawal
      const withdrawalObj = await createWithdrawal(activeVendor.name, amount, emailInput );
      // Redirect to the link provided in a new tab to finalize their withdrawal - Open the link in a new tab
      window.open(withdrawalObj.link, '_blank'); // If the API call fails, the error will be caught by the interceptor in the API file and the user will be notified with a toast modal
      // Close this Withdrawl modal after successful submission
      onClose?.();
    }
    catch (error) {
      // Handle error gracefully, no need to show toast it showed in interceptor
    }
    finally {
      // Reset submitting state (regardless of success or failure)
      setIsSubmitting(false);
    }
  };

  // Show error message for amount field
  const anountTypeErrorMessage: string = (errors[type]?.message as string) || '';

  // Determine if the submit button should be disabled
  // Disable the button if:
  // - there's any form error
  // - no amount is selected
  // - user didn't agree to non-refundable terms
  // - if PayPal: must confirm email can receive payments
  // - form is being submitted
  const isPayPal = type === PaymentType.PAYPAL;
  const disableButton =
    hasError ||
    !amount ||
    !confirmNonRefundable || 
    (isPayPal && (!confirmPayment)) ||
    isSubmitting;

  return (
    <form className={styles.wrapper}>
      {/* Title Section */}
      <Title variant={'h2'}>
        <>{title}</>
      </Title>

      {/* Top section with image and withdrawal notes */}
      <div className={styles.top}>
        <div className={classNames(styles.image, styles[type])}>
          <img src={image} alt={title} />
        </div>
        <div className={styles.col}>
          <div className={styles.row}>
            <Text variant={'medium'}>{activeVendor.description}</Text>
          </div>
        </div>
      </div>

      {/* Amount selection */}
      <div className={styles.hr}>How much do you want to withdraw?</div>
      {activeVendor ? (
        <Controller
          control={control}
          rules={{
            required: 'Select withdraw amount' // Validation rule
          }}
          render={({ field }) => {
            return (
              <div className={styles.list}>
                {activeVendor.availableAmounts.map((item: { amount: string; fee: string }, index: number) => {
                  return (
                    <Item
                      key={index}
                      vendor={activeVendor}
                      amount={item.amount}
                      fee={item.fee}
                      control={control}
                      field={field}
                      checked={amount === item.amount}
                    />
                  );
                })}
              </div>
            );
          }}
          name={type} // Name of the field, dynamic based on payment type
        />
      ) : (
        <div className={styles.list}>
          <NoData />
        </div>
      )}

      {/* Show extra form if type is PayPal */}
      {type === PaymentType.PAYPAL && <AccountNameForm />}

      {/* Withdrawal fee info */}
      <div className={styles.between}>
        <Text variant={'large'}>Withdrawal Fee</Text>
        <Text variant={'large'}>
          {activeVendor?.withdrawalFeeText}
        </Text>
      </div>

      {/* Selected amount */}
      <div className={styles.between}>
        <Text variant={'large'} nativeClassname={classNames(styles.white, styles.strong)}>
          Total Amount
        </Text>
        <Text variant={'large'} nativeClassname={classNames(styles.white, styles.strong)}>
          ${amount || 0}
        </Text>
      </div>

      {/* Divider */}
      <div className={styles.hr2}></div>

      {/* Final amount to receive after fees */}
      <div className={styles.between}>
        <Text variant={'large'} nativeClassname={styles.white}>
          You will receive
        </Text>
        <Text variant={'large'} nativeClassname={styles.white}>
          ${amountFeesSubtracted || 0}
        </Text>
      </div>

      {/* Confirmation checkboxes */}
      {type === PaymentType.PAYPAL && (
        <>
          <div className={styles.between}>
            <Checkbox
              control={control}
              label={' I confirm my PayPal can receive payments'}
              name={'confirmPayPal'}
              error={(errors['confirmPayPal']?.message as string) || ''}
              rules={{ required: 'Field is required' }}
            />
          </div>
          <div className={styles.between}>
            <Checkbox
              control={control}
              label={" I'm aware this order can not be refunded"}
              name={'confirmNonRefundable'}
              error={(errors['confirmNonRefundable']?.message as string) || ''}
              rules={{ required: 'Field is required' }}
            />
          </div>
        </>
      )}

      {type !== PaymentType.PAYPAL && (
        <div className={styles.between}>
          <Checkbox
            control={control}
            label={" I'm aware this order can not be refunded"}
            name={'confirmNonRefundable'}
            error={(errors['confirmNonRefundable']?.message as string) || ''}
            rules={{ required: 'Field is required' }}
          />
        </div>
      )}

      {/* Submit button */}
      <div className={styles.btnWrapper}>
        <Button
          colorPalette={'green'}
          onClick={handleSubmit(onSubmit)}
          className={styles.btn}
          type={'button'}
          disabled={disableButton}
        >
          Withdraw
        </Button>
        {!!anountTypeErrorMessage ? <div className={styles.error}>anountTypeErrorMessage</div> : null}
      </div>

      {/* Legal disclaimer text todo: maybe add in the future*/}
      {/* <Text variant={'smallCaption'} nativeClassname={styles.text}>
        <p>
        PayPal is not affiliated with this company and does not sponsor these rewards. All trademarks, logos, and brand names are the property of their respective owners. For complete details, please visit each company’s official website.
        </p>
      </Text> */}
    </form>
  );
};

export default Withdrawal;